import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import {
  AppSettingsService,
  GlobalEventBusService,
  hd,
  TicketContextService,
  TicketService,
  UploadService,
} from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { IFileInfo } from 'app/modules/remote-control/file-manager/fileData';
import { Validation } from 'app/utils/validation.helper';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Activity,
  ActivityTypeEnum,
  AttachmentActivity,
  UploadedFile,
  FileInfo,
  isAttachmentActivity,
  isDeviceCaptureActivity,
  isOutboundEmailActivity,
} from '../ticket.model';
import {
  WorkPanelManagerService,
  WorkPanelStatus,
  WorkPanelType,
} from 'xsmodules/remote-control/services/work-panel-manager.service';
import { DeviceCaptureActivityTypes } from '../ticket-page.service';

type IUploadConfig = hd.IUploadConfig;

/** Defines the interface for the attachment model */
export interface IAttachmentModel {
  /** The attachment id */
  attachmentId: string;

  /** The file name to be used when attachment is downloaded */
  fileName: string;

  /** The size of the file. Returned when the file is uploaded */
  fileSize?: number;

  thumbnail?: string;
}

/** Defines the interface for the attachment service configuration object */
export interface IAttachmentServiceConfig {
  /** The maximum supported file size, in megabytes */
  maxFileSize: number;
  downloadExpiryMilliseconds: number;
  maxPreviewSize: number;
}

@Injectable()
export class AttachmentService {
  readonly maxFileNameLength = 255;
  unsubscribe$ = new Subject<void>();

  private _previewActivity: Activity = null;

  constructor(
    private $http: HttpClient,
    private uploadService: UploadService,
    private appSettingsService: AppSettingsService,
    private ticketService: TicketService,
    private ticketContextService: TicketContextService,
    // private $transitions: TransitionService,
    private workPanelManagerService: WorkPanelManagerService,
    private globalEventBusService: GlobalEventBusService,
    private $filter: XsI18nPipe
  ) {
    // On state change, clear the preview attachment
    // this.$transitions.onSuccess({}, (transition: Transition) => {
    //     this.resetAttachmentPreview();
    // });
  }

  resetAttachmentPreview() {
    this._previewActivity = null;
  }

  public deleteLocalAttachmentFile(attachmentId): Observable<string> {
    return this.$http.delete<string>(this.appSettingsService.attachmentServiceUrl + `${attachmentId}`);
  }

  /** @See IAttachmentService.getConfig */
  getConfig = (): Observable<IAttachmentServiceConfig> =>
    this.$http.get<IAttachmentServiceConfig>(this.appSettingsService.attachmentServiceUrl + 'config');

  /** @See IAttachmentService.uploadAttachment */
  uploadAttachment = (file: File, displayName?: string): Observable<any> => {
    // Define file upload config
    const config: IUploadConfig = {
      url: this.getAttachmentUrl() + 'upload',
      data: {
        file,
      },
      params: {
        displayName,
      },
    };

    return this.uploadService.upload(config);

    // return new Promise<boolean>(r=>r(true));
    //  this.uploadService.upload(config);
  };

  /** @See IAttachmentService.createDownload */
  createDownload = (activity: AttachmentActivity): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      // this.$log.debug('Posting to create download for attachment to Attachment Service');
      // TODO: patch the activity coming from backend instead of checking where file exists
      let attachment: FileInfo;
      if (activity.fileName !== undefined && !activity.details) {
        attachment = {
          attachmentId: activity.fileId,
          fileName: activity.fileName,
        };
      } else {
        if (
          activity.details?.files &&
          (this.isSupportedImageType(activity.details.files[0].fileName) ||
            this.isSupportedLogType(activity.details.files[0].fileName))
        ) {
          attachment = {
            attachmentId: activity.details.files[0].fileId,
            fileName: activity.details.files[0].fileName,
          };
        } else {
          attachment = {
            attachmentId: activity.details.fileId,
            fileName: activity.details.fileName,
          };
        }
      }

      this.doCreateDownload(attachment).subscribe(
        (response: string) => {
          // Complete the async promise
          activity.$$previewId = response + '';
          const downloadUrl: string = this.appSettingsService.attachmentServiceUrl + 'download/' + activity.$$previewId;
          resolve(downloadUrl);
        },
        err => {
          reject(err);
        }
      );
    });

  doCreateDownload(fileInfo): Observable<string> {
    return this.$http.post<string>(`${this.getAttachmentUrl()}createDownload`, fileInfo, {
      responseType: 'text' as 'json',
    });
  }

  /** @See IAttachmentService.createDownloadConfig */
  createDownloadConfig = (downloadFileName: string): hd.PromptDialogParams => {
    const parts = downloadFileName.split('.');
    const extension = parts.length > 1 ? '.' + parts.pop() : '';
    const fileName = downloadFileName.substr(0, downloadFileName.lastIndexOf(extension));

    return {
      title: this.$filter.transform('prompt_dialog_title_download'),
      icon: 'hd-icon-download',
      confirmButton: this.$filter.transform('prompt_dialog_title_download'),
      inputTitle: this.$filter.transform('prompt_dialog_screenshot_FileName'),
      extension,
      placeHolder: Validation.sanitizedWindowsFileName(fileName),
      regexFilter: Validation.windowsFileNameRegex,
      unallowedChar: '\\ * ? " : | < > /',
    } as hd.PromptDialogParams;
  };

  /** @See IAttachmentService.createDownloadPromptConfig */
  createDownloadPromptConfig = (
    activity: AttachmentActivity,
    extension?: string,
    isDatetimeTobeAppendToFilename?: boolean
  ) => {
    let tempName = isDatetimeTobeAppendToFilename ? this.getDownloadFileName(activity) : activity.displayName;
    const fileName = (activity.fileName ||
      activity.details.fileName ||
      activity.details.files[0]?.fileName ||
      '') as string;

    // Trims the file extension of the filename if none has been provided.
    if (!extension) {
      const parts = fileName.split('.');
      extension = parts.length > 1 ? '.' + parts.pop() : '';
    }

    if (!tempName) {
      // Set placeholder to original filename, with the extension stripped off
      tempName = fileName;
      if (extension) {
        tempName = tempName.substr(0, tempName.lastIndexOf(extension));
      }
    }

    const dialogParams = {
      title: this.$filter.transform('prompt_dialog_title_download'),
      icon: 'hd-icon-download',
      confirmButton: this.$filter.transform('prompt_dialog_title_download'),
      inputTitle: this.$filter.transform('prompt_dialog_screenshot_FileName'),
      extension,
      placeHolder: Validation.sanitizedWindowsFileName(tempName),
      regexFilter: Validation.windowsFileNameRegex,
      unallowedChar: '\\ * ? " : | < > /',
    } as hd.PromptDialogParams;

    return {
      template: '', //require('raw-loader!../../../core/components/promptDialog/promptDialog.tpl.html'),
      //controller: PromptDialogController,
      // controllerAs: '$ctrl',
      // scope: this.$rootScope,
      backdrop: 'static',
      size: 'sm',
      resolve: {
        dialogParams,
      },
    };
  };

  /** @See IAttachmentService.createDownloadWithUserInput */
  createDownloadWithUserInput = (activity: Activity, fileSaveParams: hd.FileSaveParams): Observable<string> => {
    // Make sure we saving it with the extension if somehow filename is too long
    let validFileName: string;
    const fileNameLength = fileSaveParams.fileName ? fileSaveParams.fileName.length : 0;
    const fileExtLength = fileSaveParams.extension ? fileSaveParams.extension.length : 0;
    const length = fileNameLength + fileExtLength - this.maxFileNameLength;
    if (length >= 0) {
      validFileName = fileSaveParams.fileName.substring(0, fileSaveParams.fileName.length - length);
    } else {
      validFileName = fileSaveParams.fileName;
    }
    // TODO: Make activity model more consistent with the files object
    let files: any = [];
    if (isAttachmentActivity(activity) || isOutboundEmailActivity(activity)) {
      files = { attachmentId: activity.details.fileId, fileName: validFileName };
    } else if (isDeviceCaptureActivity(activity)) {
      const mappedFiles = activity.details.files.map(
        file => ({ attachmentId: file.fileId, fileName: file.fileName } as FileInfo)
      );
      files = mappedFiles.length === 1 ? mappedFiles[0] : { files: mappedFiles, fileName: validFileName };
    } else {
      if (activity['fileName'] !== undefined && !activity.details) {
        files = { attachmentId: activity['fileId'], fileName: activity['fileName'] } as FileInfo;
      }
    }
    return this.doCreateDownload(files).pipe(
      map((response: string) => {
        // Complete the async promise
        activity['$$previewId'] = response + '';
        return `${this.appSettingsService.attachmentServiceUrl}download/${response}`;
      })
    );
  };

  /** @See IAttachmentService.getDefaultThumbnailUrl */
  getDefaultThumbnailUrl = (): string => this.appSettingsService.attachmentServiceUrl + 'thumbnail/default';

  /** @See IAttachmentService.createDownload */
  createThumbnail = (activity: AttachmentActivity): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      const attachment: FileInfo = {
        attachmentId: activity.fileId,
        fileName: activity.fileName,
      };
      return this.doCreateDownload(attachment)
        .pipe(
          map((response: string) => {
            // Complete the async promise
            activity['$$previewId'] = response + '';
            return `${this.appSettingsService.attachmentServiceUrl}thumbnail/${response}`;
          })
        )
        .subscribe(
          response => resolve(response),
          err => reject(err)
        );
    });

  /** @See IAttachmentService.createStreamDownload */
  createStreamDownload = (activity: AttachmentActivity): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      // TODO: patch the activity coming from backend instead of checking where file exists
      let attachment: FileInfo;
      if (activity.details?.files && Object.keys(activity.details.files).includes('0')) {
        attachment = {
          attachmentId: activity.details.files[0].fileId,
          fileName: activity.details.files[0].fileName,
        };
      } else {
        attachment = {
          attachmentId: activity.details.fileId,
          fileName: activity.details.fileName,
        };
      }

      return this.doCreateDownload(attachment)
        .pipe(
          map((response: string) => {
            // Complete the async promise
            activity['$$previewId'] = response + '';
            return `${this.appSettingsService.attachmentServiceUrl}video/${activity.$$previewId}`;
          })
        )
        .subscribe(
          response => resolve(response),
          err => reject(err)
        );
    });

  /** @See IAttachmentService.extendDownloadTimeSpan */
  extendDownloadTimeSpan = (activity: AttachmentActivity): Observable<{}> =>
    this.$http.post(`${this.appSettingsService.attachmentServiceUrl}download/${activity.$$previewId}/extend`, {});

  /** @See IAttachmentService.getPreviewAttachment */
  getPreviewAttachment(): Activity {
    return this._previewActivity;
  }

  /** @See IAttachmentService.setPreviewAttachment */
  setPreviewAttachment(activity: Activity): boolean {
    let success = true;
    if (this._previewActivity !== activity) {
      this.globalEventBusService.broadcast(GlobalEvents.previewAttachment, activity);
      this._previewActivity = activity;
    } else {
      success = false;
    }
    return success;
  }

  /** @See IAttachmentService.clearPreviewAttachment */
  clearPreviewAttachment = () => {
    if (this._previewActivity != null) {
      this._previewActivity = null;
    }
    if (this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.filePreview)) {
      this.workPanelManagerService.removeActiveWorkPanelsByStatus(WorkPanelStatus.embedded);
    }
  };

  isFileTypePreviewSupported = (fileName: string): boolean =>
    this.isSupportedImageType(fileName) || this.isSupportedVideoType(fileName) || this.isSupportedLogType(fileName);

  isSupportedImageType = (fileName: string): boolean =>
    fileName && /\.(png|jpg|jpeg|jpe|jif|jfif|jfi|gif|bmp)$/i.test(fileName);

  isSupportedVideoType = (fileName: string): boolean => fileName && /\.(mp4)$/i.test(fileName);

  isSupportedLogType = (fileName: string): boolean =>
    fileName && (/\.(txt|log|xml|csv|config)$/i.test(fileName) || /\.(txt|log|xml|csv|config)+\.\d+$/i.test(fileName));

  /** @See IAttachmentService.fetchMediaUrl */
  fetchMediaUrl = (activity: AttachmentActivity, thumbnail: boolean = false): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      const isVideo = activity && activity.fileName ? this.isSupportedVideoType(activity.fileName) : false;

      // Fetch a temporary download URL for the attachment
      let promise: ng.IPromise<string>;
      if (thumbnail) {
        promise = this.createThumbnail(activity);
      } else if (isVideo) {
        promise = this.createStreamDownload(activity);
      } else {
        promise = this.createDownload(activity);
      }

      let downloadUrl: string;
      promise
        .then((url: string) => {
          downloadUrl = url;
        })
        .catch((e: hd.IError) => {
          e.isHandled = true;
          //this.$log.warn('Failed to get attachment download url');
          if (thumbnail) {
            downloadUrl = this.getDefaultThumbnailUrl();
          }
        })
        .finally(() => {
          if (thumbnail || !isVideo) {
            this.fetchImageBase64(downloadUrl)
              .then((base64Url: string) => {
                resolve(base64Url);
              })
              .catch((e: hd.IError) => {
                reject(e);
              });
          } else {
            resolve(downloadUrl);
          }
        });
    });

  /** @See IAttachmentService.fetchImageBase64 */
  fetchImageBase64 = (url: string): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              const blob = new Blob([xhr.response], { type: 'image/png' });

              this.toBase64(blob)
                .then((base64: string) => {
                  resolve(base64);
                })
                .catch((e: hd.IError) => {
                  reject('Failed to convert image blob to base64');
                });
            } else {
              reject('Attachment download failed with status: ' + xhr.status);
            }
          }
        };

        xhr.send();
      } catch (e) {
        reject('Failed to fetch data for image: ' + url);
      }
    });

  downloadFile = (fileInfo: IFileInfo): Promise<void> =>
    this.$http
      .get(fileInfo.filePath, { responseType: 'blob' })
      .toPromise()
      .then(response => {
        const binaryData = [];
        binaryData.push(response);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData));
        if (fileInfo.fileName) {
          downloadLink.setAttribute('download', fileInfo.fileName);
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();
      });

  /** @See IAttachmentService.fetchLogFileData */
  fetchLogFileData = (url: string): Observable<string> =>
    this.$http.get<string>(url, {
      responseType: 'text' as 'json',
    });

  private toBase64 = (blob: Blob): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        /* Type FileReaderProgressEvent is removed in TypeScript v3.0+ */
        resolve(e.target.result);
      };
      reader.onerror = () => {
        reject('Failed to read blob as base64');
      };
      reader.readAsDataURL(blob);
    });

  private getAttachmentUrl = (): string => {
    return this.ticketService.getTicketAttachmentUrl(this.ticketContextService?.ticket?.ticketId);
  };

  private getDownloadFileName = (activity: AttachmentActivity): string => {
    if (activity && activity.fileName) {
      const filepath = activity.fileName.split('/');
      const filename = filepath.length >= 1 ? filepath.pop() : '';
      return filename.lastIndexOf('.') !== -1 ? filename.substring(0, filename.lastIndexOf('.')) : filename;
    } else if (activity && activity.details.fileName) {
      const filepath: string[] = activity.details.fileName.split('/');
      const filename = filepath.length >= 1 ? filepath.pop() : '';
      const filenameWithoutExtension = filename.substring(0, filename.lastIndexOf('.')) || filename;
      const datetime: string = moment(new Date(activity.creationDate).toISOString()).format('YYYYMMDD[T]HH:mm:ss');
      return activity.displayName + ' ' + filenameWithoutExtension + ' ' + datetime;
    } else {
      return '';
    }
  };

  /* Gets the first file info from the attachment and device capture activities */
  getFile = (activity: Activity): UploadedFile => {
    if (!activity?.details) {
      return null;
    } else if (isDeviceCaptureActivity(activity)) {
      return activity.details?.files && activity.details.files.length > 0 ? activity.details.files[0] : null;
    } else if (isAttachmentActivity(activity)) {
      const file = (<unknown>activity.details) as UploadedFile;
      file.fileId = file.fileId || activity.fileId;
      return file;
    }
  };

  /**
   * Patches the raw activity recieved from the backend to match our model
   *
   * @param activity
   * @returns activity
   * Note: This mutates the input
   */
  patchActivity(activity: Activity) {
    if (typeof activity.details === 'object' && Object.keys(activity.details).includes('0')) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      activity.details = activity.details[0];
    }
    if (isDeviceCaptureActivity(activity)) {
      if (!activity.details.captureType) {
        // TODO possibly remove this cleanup, captureType is now assigned in the DB
        console.error(`Activity captureType for activity ${activity.activityId} should not be empty or null`, activity);
        if (activity.activityTypeId === ActivityTypeEnum.deviceVideo) {
          activity.details.captureType = DeviceCaptureActivityTypes.video;
        } else if (activity.activityTypeId === ActivityTypeEnum.deviceLog) {
          activity.details.captureType = DeviceCaptureActivityTypes.adbLogs;
        } else if (activity.activityTypeId === ActivityTypeEnum.deviceScreenshot) {
          activity.details.captureType = DeviceCaptureActivityTypes.image;
        }
      }
    }
    return activity;
  }
}
